import { useCallback, useEffect, useState } from 'react'

/**
 * FONTE DA VERDADE do seletor de idiomas: os `<link rel="alternate" hreflang>` do próprio <head>.
 *
 * O seletor montava a URL do outro idioma trocando SÓ o prefixo (`/es/x` → `/ja/x`), mantendo o
 * slug do idioma ATUAL. Em páginas com slug traduzido (seguradoras) isso produz 404 em toda troca que
 * não parta do português — o slug espanhol não existe sob /ja, nem sob caminho nenhum sem /es.
 * Só que o SERVIDOR já calcula o caminho REAL de cada idioma para o hreflang (localizedSlug +
 * cadeia pai/filho, recíproco com o serving). Em vez de duplicar essa lógica no cliente, o seletor
 * passa a LER os alternates do documento — uma fonte, zero divergência.
 *
 * Quando um idioma NÃO está nos alternates (doc sem tradução naquele locale — o hreflang é gated
 * por tradução real), o chamador cai no comportamento antigo (troca de prefixo), que nesse caso é
 * o correto: o doc serve sob /xx com o slug global e o gate 307 decide.
 */
export type HeadAlternate = { hreflang: string; href: string }

/**
 * Escolhe o alternate do locale pedido. Regras de casamento (casos reais do site):
 *  - exato, sem case: `en` ⇔ `en`, `zh-CN` ⇔ `zh-CN`;
 *  - hreflang REGIONALIZA o locale: `pt` casa `pt-BR` (o head emite pt-BR; o catálogo usa pt).
 * `x-default` nunca casa (é o fallback do Google, não um idioma).
 */
export function pickAlternateHref(
  alternates: readonly HeadAlternate[],
  targetLocale: string,
): string | null {
  const want = targetLocale.trim().toLowerCase()
  if (!want) return null
  let regional: string | null = null
  for (const a of alternates) {
    const h = (a.hreflang || '').trim().toLowerCase()
    if (!h || h === 'x-default' || !a.href) continue
    if (h === want) return a.href
    if (h.startsWith(`${want}-`) && regional == null) regional = a.href
  }
  return regional
}

/** Lê os alternates do <head> atual. Só no cliente; em SSR devolve lista vazia. */
export function readDocumentAlternates(): HeadAlternate[] {
  if (typeof document === 'undefined') return []
  return [...document.querySelectorAll('link[rel="alternate"][hreflang]')].map((l) => ({
    hreflang: l.getAttribute('hreflang') ?? '',
    href: l.getAttribute('href') ?? '',
  }))
}

/**
 * Hook do seletor: devolve `(locale) => caminho | null` a partir dos alternates da página atual.
 * Lido em efeito (pós-hidratação, re-lido a cada navegação): o 1º render usa o fallback do
 * chamador — sem mismatch de hidratação. Só aceita URLs do PRÓPRIO domínio e devolve apenas o
 * pathname (o chamador anexa a query atual, igual fazia).
 */
export function useLocaleAlternates(pathname: string): (locale: string) => string | null {
  const [alts, setAlts] = useState<HeadAlternate[]>([])
  useEffect(() => {
    setAlts(readDocumentAlternates())
  }, [pathname])
  return useCallback(
    (locale: string) => {
      const href = pickAlternateHref(alts, locale)
      if (!href) return null
      try {
        const u = new URL(href, window.location.origin)
        if (u.origin !== window.location.origin) return null
        return u.pathname
      } catch {
        return null
      }
    },
    [alts],
  )
}
